import {
  Card,
  Button,
  OverlayTrigger,
  Tooltip,
  Form,
  Row,
  Tab,
  Tabs,
  Popover,
  Col,
} from "react-bootstrap";
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import {
  ListUsersForAccountApiArg,
  useDeleteUserMutation,
  useUpdateUserMutation,
  useResetUserFiltersMutation,
  useUnlockUserMutation,
} from "@generated";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faTrashCan,
  faBarsFilter,
  faPencil,
  faGear,
} from "@fortawesome/pro-solid-svg-icons";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import SearchField from "@components/Forms/SearchField/SearchField";
import { TextField } from "@components/FormsV2";
import DataTable from "@components/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import { User } from "../common/types";
import { snakeToText } from "@utils/helperFunctions";
import useListUsers from "../common/api/useListUsers";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
const strftime = require("strftime");
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  handleAlert,
  formatUnprocessibleResponse,
} from "@utils/helperFunctions";
import SlideOutMenuV2 from "@components/SlideOutMenuV2";
import { Option } from "../common/types";
import { MultiSelectAutocomplete } from "@components/FormsV2/MultiSelectAutoComplete";

type UserType = "view_only" | "licensed" | "sales" | "service";

interface UserSelectionsState {
  name?: string;
  title?: string;
  teams?: Option[];
  roles?: Option[];
  types?: Option[];
  startDate?: string;
  endDate?: string;
}

const Show = ({ account, teams, roles, editor }) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  const navigate = useNavigate();
  const [update] = useUpdateUserMutation();
  const [archive] = useDeleteUserMutation();
  const [resetUserFilters] = useResetUserFiltersMutation();
  const [unlockUser] = useUnlockUserMutation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState<boolean>(true);
  const [filterSlideoutOpen, setFilterSlideoutOpen] = useState<boolean>(false);
  const [filterParams, setFilterParams] = useState<
    ListUsersForAccountApiArg["filter"]
  >({});
  const [userSelections, setUserSelections] = useState<UserSelectionsState>({
    title: "",
    teams: [],
    roles: [],
    types: [],
    startDate: "",
    endDate: "",
  });
  const [filterCount, setFilterCount] = useState<number>(0);
  const [applyFilters, setApplyFilters] = useState<boolean>(false);
  const [clearAll, setClearAll] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);

  const { users, isLoading, isFetching, pageMeta, refetch } = useListUsers({
    filter: { ...filterParams, active: activeTab },
    page: { number: page, size: pageSize },
  });

  const handleArchive = () => {
    if (!selectedIds.length) return;
    const promises = selectedIds.map((id) => {
      return (activeTab ? archive : update)({
        slug: accountSlug,
        id: Number(id),
        body: {
          data: {
            id: Number(id),
            type: "users",
            //@ts-ignore
            attributes: { active: !activeTab },
          },
        },
      });
    });

    if (promises.length) {
      Promise.all(promises)
        .then((res) => {
          //@ts-ignore
          const error = res?.[0]?.error;
          if (error) {
            setErrorMessage(
              //@ts-ignore
              res[0].error?.data?.exception || "Something went wrong"
            );
            setShowFailAlert(true);
          } else {
            const action = activeTab ? "archived" : "restored";
            setSuccessMessage(
              selectedIds.length <= 1
                ? `User has been ${action}.`
                : `${selectedIds.length} users ${action}.`
            );
            setShowSuccessAlert(true);
          }
        })
        .catch((error) => {
          setErrorMessage(formatUnprocessibleResponse(error, "user"));
          setShowFailAlert(true);
        })
        .finally(() => {
          setLoading(false);
          setSelectedIds([]);
          setShowConfirmationModal(false);
          refetch();
        });
    }
  };

  const handleResetFilters = (id) => {
    resetUserFilters({
      slug: accountSlug,
      id: Number(id),
    }).then((res) => {
      if ("error" in res) {
        setErrorMessage("Could not reset filters for user.");
        setShowFailAlert(true);
      } else {
        setSuccessMessage("User data filters successfully reset.");
        setShowSuccessAlert(true);
      }
    });
  }

  const handleUnlockUser = (id) => {
    unlockUser({
      slug: accountSlug,
      id: Number(id),
    }).then((res) => {
      if ("error" in res) {
        setErrorMessage("Could not unlock user.");
        setShowFailAlert(true);
      } else {
        setSuccessMessage("User account unlocked.");
        setShowSuccessAlert(true);
      }
    });
  }

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    searchName(searchInput);
  };

  const searchName = (input: string) => {
    setPage(1);
    setFilterParams({ ...filterParams, name: input });
  };

  const handleTabSelect = (eventKey) => {
    setPage(1);
    setActiveTab(Number(eventKey) === 1);
    setSelectedIds([]);
  };

  const handleCancel = () => {
    setPage(1);
    setShowConfirmationModal(false);
    setSelectedIds([]);
  };

  useEffect(() => {
    refetch();
  }, [activeTab]);

  useEffect(() => {
    let count = 0;
    for (let key in userSelections) {
      if (
        Array.isArray(userSelections[key]) &&
        userSelections[key].length > 0
      ) {
        count++;
      }
    }
    if (userSelections.title) count++;
    if (userSelections.startDate || userSelections.endDate) count++;

    setFilterCount(count);
  }, [userSelections]);

  useEffect(() => {
    if (clearAll && !filterSlideoutOpen) {
      setUserSelections({
        title: "",
        teams: [],
        roles: [],
        types: [],
        startDate: "",
        endDate: "",
      });
      //UPDATE filter params
      setFilterParams({ name: searchInput || undefined });
      setClearAll(false);
      setPage(1);
      setApplyFilters(true);
    }
  }, [filterSlideoutOpen]);

  useEffect(() => {
    if (applyFilters) {
      if (
        (userSelections.startDate && !userSelections.endDate) ||
        (!userSelections.startDate && userSelections.endDate)
      ) {
        setErrorMessage("Please provide a start and end date");
        setShowFailAlert(true);
        setApplyFilters(false);
        return;
      }
      let params: ListUsersForAccountApiArg["filter"] = { name: searchInput };
      if (userSelections.title) params.title = userSelections.title;
      if (userSelections.teams?.length)
        params.team = userSelections.teams.map((t) => t.value).join(",");
      if (userSelections.roles?.length)
        params.role = userSelections.roles.map((r) => r.value).join(",");
      if (userSelections.types?.length)
        //@ts-ignore
        params["user-type"] = userSelections.types
          .map((t) => t.value)
          .join(",");
      if (userSelections.startDate)
        params["created-at.after"] = userSelections.startDate;
      if (userSelections.endDate)
        params["created-at.before"] = userSelections.endDate;

      setFilterParams(params);
      setFilterSlideoutOpen(false);
      setApplyFilters(false);
    }
  }, [applyFilters]);

  const columns: ColumnDef<User>[] = [
    {
      header: "Name",
      cell(props) {
        const item = props.row.original;
        return item.id === 0 ? (
          item.name
        ) : (
          <div
            style={{ color: "#418172", textDecoration: "underline" }}
            onClick={() => navigate(`edit/${item.id}`)}
          >
            {item.name}
            <FontAwesomeIcon icon={faPencil} style={{ marginLeft: "10px" }} />
          </div>
        );
      },
    },
    {
      header: "Title",
      cell(props) {
        return <div className="title">{props.row.original?.title || ""}</div>;
      },
    },
    {
      header: "Email",
      cell(props) {
        return <div className="email">{props.row.original.email || ""}</div>;
      },
    },
    {
      header: "Phone",
      cell(props) {
        return <div className="phone">{props.row.original?.phone || ""}</div>;
      },
    },
    {
      header: "Added On",
      cell(props) {
        const createdAtStr = props.row.original?.createdAt || "";
        return (
          <div className="addedOn">
            {createdAtStr
              ? strftime(
                  account?.attributes?.["short-date-format"] || "%m/%d/%Y",
                  new Date(createdAtStr)
                )
              : ""}
          </div>
        );
      },
    },
    {
      header: "Team",
      cell(props) {
        return (
          <div className="team">
            {props.row.original.teams?.map((t) => t.label)?.join(", ") || ""}
          </div>
        );
      },
    },
    {
      header: "Role",
      cell(props) {
        const roles = props.row.original.roles;
        const type = props.row.original.userType || "";
        return (
          <div className="userRole">
            <span>{roles?.map((r) => r.label)?.join(", ")}</span>
            {type && (
              <span
                style={{
                  display: "flex",
                  fontSize: "12px",
                  fontWeight: "700",
                }}
              >
                {snakeToText(type)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: " ",
      cell(props) {
        const user = props.row.original;
        const type = user.userType;
        return user.id === 0 ? (
          <></>
        ) : (
          <div className="userTeam">
            <OverlayTrigger
              trigger="click"
              placement="left"
              rootClose
              overlay={
                <Popover id={`popover-${user.id}`}>
                  <Popover.Body>
                    {["licensed", "sales", "service", "view_only", "archive"]
                      .filter((opt) => opt !== type)
                      .map((opt) => (
                        <div
                          key={opt}
                          style={{ cursor: "pointer", color: "#1c2655" }}
                          onClick={() => {
                              handleConvertUserType(user, opt as UserType);
                          }}
                        >
                        { `Convert to ${snakeToText(opt)} User` }
                        </div>
                      ))}
                      <hr />
                      <div
                        key={"resetFilters"}
                        style={{ cursor: "pointer", color: "#1c2655" }}
                        onClick={() => {
                          handleResetFilters(user.id)
                        }}
                      >
                        Reset Data Filters
                      </div>
                      <div
                        key={"unlock"}
                        style={{ cursor: "pointer", color: "#1c2655" }}
                        onClick={() => {
                          handleUnlockUser(user.id)
                        }}
                      >
                        Unlock User
                      </div>
                      <hr />
                      <div
                        key={"archive"}
                        style={{ cursor: "pointer", color: "#1c2655" }}
                        onClick={() => {
                          setSelectedIds([String(user.id)]);
                          setShowConfirmationModal(true);
                        }}
                      >
                        Archive
                      </div>
                  </Popover.Body>
                </Popover>
              }
            >
              <FontAwesomeIcon icon={faGear} style={{ fontSize: "20px" }} />
            </OverlayTrigger>
          </div>
        );
      },
    },
  ];
  const handleConvertUserType = async (user: User, type: UserType) => {
    try {
      await update({
        slug: accountSlug,
        id: Number(user.id),
        body: {
          data: {
            type: "users",
            id: Number(user.id),
            attributes: {
              name: user.name,
              email: user.email || "",
              "user-type": type,
            },
          },
        },
      }).unwrap();
      refetch();
    } catch (error) {
      console.error("Failed to update user type:", error);
    }
  };

  const usersLoading = isLoading || isFetching;

  const renderTable = () =>
    usersLoading ? (
      <SmallSpinner />
    ) : (
      <DataTable
        data={users?.length ? users : [{ id: 0, name: "No users found" }]}
        columns={activeTab ? columns : columns.slice(0, -1)}
        selectable={users.length ? true : false}
        bordered
        striped
        hover
        totalPages={pageMeta.pageCount}
        totalRows={pageMeta.recordCount}
        currentPage={page}
        setCurrentPage={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        paginationEnabled
        pageSizeEnabled
      />
    );

  return (
    <div className="userTables profServicesV2">
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <div className="headerContainer">
        <div className="headerText">Users</div>
        <div className="actionBtns">
          <Button
            className="squareGreenButton"
            onClick={() => navigate("create")}
          >
            Add a User
          </Button>
        </div>
      </div>
      <div className="searchAndFilter" style={{ paddingLeft: "0px" }}>
        {selectedIds.length > 0 && (
          <div className="selectedServices">
            <div className="selectedMessage">{selectedIds.length} Selected</div>
            <div className="selectedIcons">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip>{activeTab ? "Archive" : "Un-archive"}</Tooltip>
                }
              >
                <FontAwesomeIcon
                  icon={activeTab ? faTrashCan : faClockRotateLeft}
                  onClick={() => setShowConfirmationModal(true)}
                  className="ctaIcon"
                />
              </OverlayTrigger>
            </div>
          </div>
        )}
        <div className={`search ${selectedIds.length > 0 ? "selected" : ""}`}>
          <FormFieldLabel label="Search Users" className={undefined} />
          <Form
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch(e);
              }
            }}
            onSubmit={handleSearch}
          >
            <SearchField
              placeholder="Search by name"
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              value={searchInput}
              onClick={(e) => {
                setSearchInput("");
                searchName("");
              }}
              fullWidth
            />
          </Form>
        </div>
        <div className="filter">
          <FontAwesomeIcon
            icon={faBarsFilter}
            onClick={() => setFilterSlideoutOpen(true)}
            className={`${filterCount ? "activeFilters" : ""}`}
          />
          {filterCount ? (
            <span className="activeFilters">{`${filterCount}`} Active</span>
          ) : null}
        </div>
      </div>
      <div className="usersTableTabsWrapper">
        <Tabs
          defaultActiveKey={1}
          id="uncontrolled-tab"
          className="mb-3"
          onSelect={handleTabSelect}
        >
          <Tab
            eventKey={1}
            title={`Active ${
              activeTab && pageMeta?.recordCount && !usersLoading
                ? `(${pageMeta?.recordCount})`
                : ""
            }`}
            tabClassName="usersTab"
            className="usersTabContent activeUsers"
          >
            {activeTab && renderTable()}
          </Tab>
          <Tab
            eventKey={2}
            title={`Archived ${
              !activeTab && pageMeta?.recordCount && !usersLoading
                ? `(${pageMeta?.recordCount})`
                : ""
            }`}
            tabClassName="usersTab"
            className="usersTabContent archivedUsers"
          >
            {!activeTab && renderTable()}
          </Tab>
        </Tabs>
      </div>
      <ScopeStackModal
        show={showConfirmationModal}
        modalTitle={`${activeTab ? "Archive" : "Un-archive"} user(s)`}
        modalBody={
          loading ? (
            <SmallSpinner />
          ) : (
            <div className="modalBody">
              <p>
                <strong>{`You are about to ${
                  activeTab ? "archive" : "un-archive"
                } ${selectedIds.length} user${
                  selectedIds.length > 1 ? "s" : ""
                }.`}</strong>
              </p>
              <p>
                This can be undone from the {activeTab ? "Archived" : "Active"}{" "}
                tab.
              </p>
            </div>
          )
        }
        button1Text="Cancel"
        handleButton1Click={handleCancel}
        button2Text={activeTab ? "Archive" : "Un-archive"}
        handleButton2Click={() => {
          setLoading(true);
          handleArchive();
        }}
        button2Disabled={loading}
        handleClose={handleCancel}
      />
      <SlideOutMenuV2
        isOpen={filterSlideoutOpen}
        onClose={() => setFilterSlideoutOpen(false)}
        onSave={() => {
          setPage(1);
          setApplyFilters(true);
        }}
        onClearAll={() => {
          setUserSelections({
            title: "",
            teams: [],
            roles: [],
            types: [],
            startDate: "",
            endDate: "",
          });
          setFilterSlideoutOpen(false);
          setPage(1);
          setApplyFilters(true);
        }}
        title="Filter By"
        filterCount={filterCount}
        actionBtnText="Apply"
      >
        <TextField
          label="Title"
          id="title"
          placeholder="Search by Title"
          className="titleField"
          value={userSelections.title}
          onChange={(e) =>
            setUserSelections({ ...userSelections, title: e.target.value })
          }
        />

        <MultiSelectAutocomplete
          label="Team"
          options={teams.map((t) => {
            return { label: t.attributes.name, value: t.id };
          })}
          value={userSelections.teams || []}
          onChange={(e) =>
            //@ts-ignore
            setUserSelections({ ...userSelections, teams: e })
          }
        />
        <MultiSelectAutocomplete
          label="Role"
          options={roles.map((r) => {
            return { label: r.attributes.name, value: r.id };
          })}
          value={userSelections.roles || []}
          onChange={(e) =>
            //@ts-ignore
            setUserSelections({ ...userSelections, roles: e })
          }
        />
        <MultiSelectAutocomplete
          label="Type"
          options={[
            { label: "Licensed", value: "licensed" },
            { label: "Sales", value: "sales" },
            { label: "Service", value: "service" },
            { label: "View Only", value: "view_only" },
          ]}
          value={userSelections.types || []}
          onChange={(e) =>
            //@ts-ignore
            setUserSelections({ ...userSelections, types: e })
          }
        />
        <div className="searchByDate">
          <div style={{ width: "40%" }}>
            <FormFieldLabel label="Date Added Start" className="" />
            <Form.Control
              type="date"
              value={userSelections.startDate || ""}
              onChange={(e) =>
                //@ts-ignore
                setUserSelections({
                  ...userSelections,
                  startDate: e.target.value,
                })
              }
            />
          </div>
          <span>To</span>
          <div style={{ width: "40%" }}>
            <FormFieldLabel label="Date Added End" className="" />
            <Form.Control
              type="date"
              value={userSelections.endDate || ""}
              onChange={(e) =>
                //@ts-ignore
                setUserSelections({
                  ...userSelections,
                  endDate: e.target.value,
                })
              }
            />
          </div>
        </div>
      </SlideOutMenuV2>
    </div>
  );
};

export default Show;
